<template>
  <div>
    <el-card class="chart-card">
      <h1>Pignoo与Spring结合</h1>
      <p>
        <el-text>Spring有优秀的事务管理机制，相比之下，Gru显得有些简陋了。Gru最大的优势在于轻量、简单，很契合Pignoo的应用场景。</el-text>
        <el-text>但是，项目本身如果运行在Spring环境下，配合Spring的事务管理机制，可以让代码更加简洁高效，因此引入了pignoo-spring。</el-text>
      </p>
      <p>
        <el-text tag="b">pignoo-spring是独立于pignoo-core之外的项目，需要单独引入：</el-text>
      </p>
      <div>
        <pre><code class="language-xml">{{ code1 }}</code></pre>
      </div>
      <p>
        <el-text>pignoo-spring里引入了Pignoo接口的新实现：SpringPignoo。它与BasePignoo不同，SpringPignoo是线程安全且单例的。</el-text>
      </p>
      <p>
        <el-text>也就是说，每一个数据源，只需要构建一个SpringPignoo，大部分单数据源项目只需要构建一个SpringPignooBean放入SpringIoC中，</el-text>
        <el-text>就可以在任意业务代码处注入Pignoo并使用了，非常方便。</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code2 }}</code></pre>
      </div>
      <div>
        <pre><code class="language-java">{{ code3 }}</code></pre>
      </div>
      <p>
        <el-text tag="b">事务标在API上，这是否合适？</el-text>
        <el-text>首先，这么写事务是可以生效的。</el-text>
        <br />
        <el-text>然后，我想说的是：如果是简单的业务场景，代码分层会带来更多的繁杂性（我就简简单单写个库，为什么非要还写Service和Dao呢？）</el-text>
        <el-text>Pignoo给你带来的，是无需Dao层的声明，可以直接模拟List操作数据库。</el-text>
        <el-text>而你需要释怀的，是在简单的业务场景下，真的没有必要那么多分层。</el-text>
        <el-text tag="b">遇到复用次数多的功能片段，当然要提炼到“Service”中；但如果一段代码逻辑独此一份，无需复用，自然也没有了提炼的必要。</el-text>
        <el-text>相信我，在小型项目中做，会给你带来无尽快乐。</el-text>
      </p>
      <p>
        <el-text>Spring的事务管理的全部功能都是支持的，无论是编程式还是声明式，无论是隔离级别还是传播行为。因为这些都是托管给Spring控制的。</el-text>
      </p>
      <h1>特别注意：pignoo-spring的一个“问题”</h1>
      <p>
        <el-text>由于Spring非事务环境下，没有类似事务的“生命周期钩子”获取提交动作，Pignoo无法明确感知当前线程已经处理完毕了一串完整业务。</el-text>
        <el-text>又由于setter代理的存在，这导致非事务环境下产生的代理对象无法退出生命周期。直到代理对象被JVM回收前，setter代理功能是一直生效的。</el-text>
        <el-text>（哦，对了，一直生效是指setter代理功能不会关闭。不是会产生内存泄露，请放心）</el-text>
      </p>
      <p>
        <el-text>简言之：</el-text>
        <el-text tag="b">非事务环境下的setter代理会一直生效！</el-text>
        <el-text>例如：</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code4 }}</code></pre>
      </div>
      <p>
        <el-text>解决方案：</el-text>
        <el-text tag="b">【不要在非事务环境操作数据库】</el-text>
        <el-text>或</el-text>
        <el-text tag="b">【不要把writer查询的结果传递到方法外】</el-text>
      </p>
      <p>
        <el-text>于是，之前章节提到过的这句话含金量还在提升：</el-text>
        <el-text tag="b">writer的查询是为修改而做的查询！如果只是为了读取数据，请使用reader！</el-text>
      </p>
      <h1>小声哔哔：</h1>
      <p>
        <el-text tag="b">SpringPignoo仅支持JDBC环境，不支持Reactive编程。</el-text>
        <el-text>因为SpringPignoo依托于org.springframework.transaction与ThreadLocal，如果在Reactive环境下（如R2DBC）事务控制会完全失效。</el-text>
        <el-text>想必，如果你正在用或是需要用Reactive编程，一定知道我在说什么。如果你不知道，那你就完全不用在意我哔哔什么了，你可以放心使用。</el-text>
      </p>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'

onMounted(() => {
  if (window.hljs) {
    window.hljs.highlightAll()
  }
})

const code1 = `<dependency>
  <groupId>com.xuesinuo</groupId>
  <artifactId>pignoo-spring</artifactId>
  <version>1.1.6</version>
</dependency>`

const code2 = `// 声明PignooBean
@Configuration
public class SpringPignooConfig {
  @Bean
  Pignoo pignoo(@Autowired DataSource dataSource) {
    return new SpringPignoo(dataSource/* , pignooConfig */);
  }
}`

const code3 = `// 在Spring环境下使用：用一个WebApi举例
@RestController
public class PigController {
  @Autowired private final Pignoo pignoo;

  @Transactional // 事务标在API上，这是否合适？
  @PostMapping("/pig")
  public void addPig(@RequestBody Pig pig) {
    pignoo.writer(Pig.class).add(Pig);
  }
}`

const code4 = `
@RestController
public class PigController {
  @Autowired private final Pignoo pignoo;

  // 没有开启事务，setter代理不会退出
  @GetMapping("/pig")
  public Pig getPig(@RequestParam Long id) {// 将setter代理作为API的返回值
    var writer = pignoo.writer(Pig.class); // 使用writer，会触发setter代理
    return writer.filter(Pig::getId, "==", id).getAny();
    // 这种情况，过滤器或拦截器中获取Pig并调用setter方法，会导致修改数据库！！
    // 例如：在拦截器中统一为password字段做set(null)处理，会导致密码真的变成空
  }
}`

</script>

<style lang="less"></style>